import React, { useState, useMemo } from "react";
import { Zap } from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { billingAPI } from "../../services/api/billing";
import { useToast } from "../../contexts/ToastContext";

const CREDITS_PER_DOLLAR = 25; // $1 = 25 credits
const MIN_CREDITS = 25;
const PRESETS = [100, 500, 2500, 10000];

export default function BuyCreditsModal({ open, onClose }) {
  const { showToast } = useToast();
  const [credits, setCredits] = useState(500);
  const [loading, setLoading] = useState(false);

  const qty = Number.parseInt(credits, 10);
  const valid = Number.isInteger(qty) && qty >= MIN_CREDITS;
  const price = useMemo(() => (valid ? qty / CREDITS_PER_DOLLAR : 0), [valid, qty]);

  const buy = async () => {
    if (!valid) return;
    try {
      setLoading(true);
      const { checkout_url } = await billingAPI.startCustomTopUpCheckout(qty);
      window.location.href = checkout_url;
    } catch (e) {
      showToast(e?.response?.data?.error || "Could not start checkout", "error");
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Modal title="Buy add-on credits" onClose={onClose} maxWidth="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Pay as you go — buy any number of credits.{" "}
          <span className="font-medium text-foreground">
            $1 = {CREDITS_PER_DOLLAR} credits
          </span>
          .
        </p>

        <div className="flex flex-wrap gap-2">
          {PRESETS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCredits(c)}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                qty === c
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:bg-muted/50"
              }`}
            >
              {c.toLocaleString()}
            </button>
          ))}
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Credits
          </label>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 focus-within:ring-2 focus-within:ring-primary/30">
            <Zap className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <input
              type="number"
              min={MIN_CREDITS}
              step={1}
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              className="w-full bg-transparent text-sm text-foreground border-0 p-0 appearance-none focus:outline-none focus:ring-0"
            />
          </div>
          {!valid && (
            <p className="text-xs text-red-600 mt-1">
              Minimum {MIN_CREDITS} credits.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
          <span className="text-sm text-muted-foreground">You pay</span>
          <span className="text-xl font-bold text-foreground">
            ${price.toFixed(2)}
          </span>
        </div>

        <Button
          variant="primary"
          className="w-full justify-center text-sm"
          disabled={!valid || loading}
          onClick={buy}
        >
          {loading
            ? "Redirecting…"
            : `Buy ${valid ? qty.toLocaleString() : ""} credits`}
        </Button>
      </div>
    </Modal>
  );
}
